import React from 'react';
import usePageTitle from '../../hooks/usePageTitle';
import { Link } from 'react-router-dom';
import Box from '../../components/Common/Box';
import { KaneaLogo } from '../../components/Common/KaneaLogo';
import MainLayout from '../../layouts/MainLayout';

// ─── Style helpers ────────────────────────────────────────────────────────────

const sectionStyle: React.CSSProperties = { marginBottom: '30px' };
const h2Style: React.CSSProperties = { fontSize: '20px', color: 'var(--text-heading)', marginBottom: '15px' };
const mutedStyle: React.CSSProperties = { color: 'var(--text-muted)', fontSize: '13px' };

const legalBox: React.CSSProperties = {
    background: 'rgba(99, 102, 241, 0.06)',
    border: '1px solid rgba(99, 102, 241, 0.2)',
    borderRadius: '10px',
    padding: '20px 24px',
    marginBottom: '10px',
    lineHeight: '1.8',
    fontSize: '14px',
};

// ─── Component ────────────────────────────────────────────────────────────────

const TermsScreen: React.FC = () => {
  usePageTitle("Conditions générales d'utilisation");
    return (
        <MainLayout>
            <div style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 20px' }}>
                <Box>
                    <div className='box-vertical-padding box-horizontal-padding'>

                        {/* ── Header ── */}
                        <div style={{ marginBottom: '30px', textAlign: 'center' }}>
                            <KaneaLogo
                                variant="horizontal"
                                title="KaneaBourse"
                                height={42}
                                markColor="#6C3FC4"
                                style={{ marginBottom: '20px' }}
                            />
                            <h1 style={{ fontSize: '28px', color: 'var(--text-heading)', marginBottom: '10px' }}>
                                Conditions Générales d'Utilisation
                            </h1>
                            <p style={mutedStyle}>Dernière mise à jour : Octobre 2026</p>
                        </div>

                        <div style={{ lineHeight: '1.8', color: 'var(--text-main)' }}>

                            {/* ── Mentions légales ── */}
                            <section style={sectionStyle}>
                                <h2 style={h2Style}>Mentions légales</h2>
                                <div style={legalBox}>
                                    <p style={{ margin: '0 0 6px 0' }}>
                                        <strong>Éditeur :</strong> KANEA TECHNOLOGIES — RCCM n° 28463/GTCA/RC/2026
                                    </p>
                                    <p style={{ margin: '0' }}>
                                        <strong>Contact :</strong> contact@kaneabourse.com
                                    </p>
                                </div>
                            </section>

                            {/* ── 1. Objet ── */}
                            <section style={sectionStyle}>
                                <h2 style={h2Style}>1. Objet</h2>
                                <p>
                                    Les présentes Conditions Générales d'Utilisation (CGU) régissent l'accès et l'utilisation
                                    de la plateforme <strong>KaneaBourse</strong>, service d'information, d'analyse et de data-visualisation
                                    du marché boursier de la Bourse Régionale des Valeurs Mobilières (BRVM), édité par <strong>KANEA TECHNOLOGIES</strong>.
                                </p>
                                <p style={{
                                    marginTop: '12px',
                                    background: 'rgba(59,130,246,0.08)',
                                    color: '#1e40af',
                                    padding: '14px 16px',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(59,130,246,0.25)',
                                    lineHeight: '1.7',
                                }}>
                                    <strong>ℹ️ Indépendance :</strong> KaneaBourse est une plateforme d'analyse indépendante,{' '}
                                    <strong>non affiliée à la BRVM</strong> ni accréditée par celle-ci.
                                    Les données de marché utilisées sont issues des publications officielles de la BRVM à titre de référence.
                                </p>
                            </section>

                            {/* ── 2. Acceptation ── */}
                            <section style={sectionStyle}>
                                <h2 style={h2Style}>2. Acceptation des conditions</h2>
                                <p>
                                    En créant un compte et en utilisant nos services, vous acceptez sans réserve les présentes
                                    conditions. Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser la plateforme.
                                </p>
                            </section>

                            {/* ── 3. Services ── */}
                            <section style={sectionStyle}>
                                <h2 style={h2Style}>3. Description des services</h2>
                                <p>KaneaBourse propose les services suivants :</p>
                                <ul style={{ marginTop: '10px', paddingLeft: '25px' }}>
                                    <li>Consultation des cotations boursières BRVM</li>
                                    <li>Analyses et indicateurs techniques (RSI, Bollinger, MACD, OBV, CCI, Stochastique…)</li>
                                    <li>Portefeuille virtuel de simulation</li>
                                    <li>Alertes techniques personnalisées</li>
                                    <li>Actualités du marché BRVM</li>
                                    <li>Calendrier des assemblées générales et dividendes</li>
                                    <li>Guide des indicateurs techniques (ressource pédagogique)</li>
                                </ul>
                            </section>

                            {/* ── 4. Compte ── */}
                            <section style={sectionStyle}>
                                <h2 style={h2Style}>4. Compte utilisateur</h2>
                                <p>
                                    Vous êtes responsable de la confidentialité de vos identifiants de connexion.
                                    Toute activité effectuée depuis votre compte est réputée être de votre fait.
                                    Vous vous engagez à nous notifier immédiatement toute utilisation non autorisée
                                    de votre compte.
                                </p>
                            </section>

                            {/* ── 5. Portefeuille virtuel ── */}
                            <section style={sectionStyle}>
                                <h2 style={h2Style}>5. Portefeuille virtuel</h2>
                                <p style={{
                                    background: 'rgba(245, 158, 11, 0.15)',
                                    color: '#d97706',
                                    padding: '15px',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(245, 158, 11, 0.3)',
                                }}>
                                    <strong>⚠️ Avertissement important :</strong> Le portefeuille virtuel est un outil de
                                    simulation à but éducatif uniquement. Les transactions effectuées ne représentent pas
                                    de véritables opérations boursières et n'impliquent aucun mouvement de fonds réels.
                                </p>
                            </section>

                            {/* ── 6. Avertissement légal ── */}
                            <section style={sectionStyle}>
                                <h2 style={h2Style}>6. Avertissement Légal &amp; Limitation de responsabilité</h2>
                                <div style={{
                                    background: 'rgba(124,58,237,0.07)',
                                    border: '1px solid rgba(124,58,237,0.2)',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    lineHeight: '1.7',
                                    color: 'var(--text-main)',
                                }}>
                                    <p style={{ margin: '0 0 10px 0' }}>
                                        <strong>AVERTISSEMENT LÉGAL :</strong> Les informations, analyses et signaux fournis par{' '}
                                        <strong>KaneaBourse</strong> sont générés par des modèles algorithmiques et d'intelligence artificielle (Nia).
                                        Ils sont fournis par <strong>KANEA Technologies</strong> à titre purement informatif et ne constituent
                                        en aucun cas un conseil en investissement, une sollicitation ou une recommandation d'achat ou de vente.
                                    </p>
                                    <p style={{ margin: '0 0 10px 0' }}>
                                        <strong>KANEA Technologies</strong> n'est pas une Société de Gestion et d'Intermédiation (SGI) ni un
                                        Conseiller en Investissement Financier (CIF) agréé par l'AMF-UEMOA.{' '}
                                        <strong>KaneaBourse</strong> est une plateforme d'analyse indépendante,{' '}
                                        <strong>non affiliée à la BRVM</strong> ni accréditée par celle-ci.
                                        Les données de marché référencées sont issues des publications officielles de la BRVM à titre de référence.
                                    </p>
                                    <p style={{ margin: '0 0 10px 0' }}>
                                        L'investissement en bourse comporte des risques de perte en capital. Les performances passées ne
                                        préjugent pas des performances futures. L'investisseur est seul responsable de l'interprétation
                                        des informations fournies et doit s'assurer qu'il dispose des connaissances nécessaires avant toute
                                        opération sur le Marché Financier Régional de l'UMOA.
                                    </p>
                                    <p style={{ margin: '0' }}>
                                        La responsabilité de KANEA Technologies ne saurait être engagée en cas d'erreur de donnée,
                                        d'omission ou de décision d'investissement inopportune.
                                    </p>
                                </div>
                            </section>

                            {/* ── 7. Propriété intellectuelle ── */}
                            <section style={sectionStyle}>
                                <h2 style={h2Style}>7. Propriété intellectuelle</h2>
                                <p>
                                    L'ensemble du contenu de la plateforme (textes, graphiques, logos, icônes, images,
                                    logiciels, algorithmes) est la propriété exclusive de <strong>KANEA Technologies</strong> ou de ses
                                    partenaires, et est protégé par les lois relatives à la propriété intellectuelle en vigueur
                                    dans les États membres de l'UEMOA. Toute reproduction, même partielle, est interdite sans
                                    autorisation préalable écrite.
                                </p>
                            </section>

                            {/* ── 8. Données personnelles ── */}
                            <section style={sectionStyle}>
                                <h2 style={h2Style}>8. Protection des données personnelles</h2>
                                <p>
                                    Nous nous engageons à protéger vos données personnelles conformément à la réglementation
                                    en vigueur. Les données collectées sont utilisées uniquement pour le fonctionnement du
                                    service. Elles ne sont ni vendues ni cédées ; elles ne sont transmises qu'aux prestataires
                                    techniques nécessaires au service.
                                </p>
                                <p style={{ marginTop: '10px' }}>
                                    Le détail des données collectées, de leurs usages, des prestataires concernés et de vos droits
                                    figure dans notre <Link to="/confidentialite">Politique de confidentialité</Link>.
                                </p>
                            </section>

                            {/* ── 9. Intelligence Artificielle ── */}
                            <section style={sectionStyle}>
                                <h2 style={h2Style}>9. Utilisation de l'Intelligence Artificielle (Nia)</h2>
                                <p>
                                    L'Utilisateur est informé que l'application intègre <strong>Nia</strong>, notre intelligence
                                    artificielle. Son nom signifie « Intention » en Swahili et « Lumière » en Celte. Elle a été
                                    conçue pour éclairer vos décisions financières avec précision.
                                </p>
                                <p style={{ marginTop: '10px' }}>
                                    Certains contenus (analyses, résumés, tendances) sont générés par Nia. Bien que KaneaBourse
                                    mette tout en œuvre pour vérifier l'exactitude des données via une supervision humaine, des
                                    erreurs ou des omissions peuvent survenir. L'Utilisateur reconnaît utiliser ces informations
                                    comme une aide à la décision et non comme une vérité absolue.
                                </p>
                            </section>

                            {/* ── 10. Modification des conditions ── */}
                            <section style={sectionStyle}>
                                <h2 style={h2Style}>10. Modification des conditions</h2>
                                <p>
                                    Nous nous réservons le droit de modifier les présentes CGU à tout moment.
                                    Les utilisateurs seront informés de toute modification par email ou notification
                                    dans l'application. L'utilisation continue du service après modification vaut
                                    acceptation des nouvelles conditions.
                                </p>
                            </section>

                            {/* ── 11. Contact ── */}
                            <section style={sectionStyle}>
                                <h2 style={h2Style}>11. Contact &amp; Service juridique</h2>
                                <p>
                                    Pour toute question concernant ces conditions ou pour exercer vos droits,
                                    vous pouvez nous contacter à :
                                </p>
                                <p style={{ marginTop: '10px' }}>
                                    📧 <strong>contact@kaneabourse.com</strong>
                                </p>
                                <p style={{ ...mutedStyle, marginTop: '8px' }}>
                                    KANEA TECHNOLOGIES — RCCM n° 28463/GTCA/RC/2026 — Côte d'Ivoire
                                </p>
                            </section>

                        </div>

                        <div style={{ marginTop: '40px', textAlign: 'center', paddingTop: '20px', borderTop: '1px solid var(--border-color)' }}>
                            <Link
                                to="/members/signup"
                                style={{
                                    color: '#667eea',
                                    textDecoration: 'none',
                                    fontWeight: '500',
                                }}
                            >
                                ← Retour à l'inscription
                            </Link>
                        </div>
                    </div>
                </Box>
            </div>
        </MainLayout>
    );
};

export default TermsScreen;
